import React, {
  memo,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useState,
} from "react";
import { Container, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { symbolData, isCoinSymbol } from "../../SymbolData";
import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import HoldAndWinCellController from "./HoldAndWinCellController";
import { createCoinLabel, setCoinLabel } from "./coinLabel";

/**
 * HoldAndWinCell — one cell of the Cleopatra's Fire Hold & Win grid.
 *
 * Two layers under one cell-sized mask:
 *   - the STATIC sprite (+ coin value): what the cell shows at rest;
 *   - the SPINNING strip, owned by HoldAndWinCellController.
 *
 * A spin hides the static layer and runs the strip; on landing the strip
 * hides itself and the static layer shows the landed symbol and value.
 *
 * Positioned by its parent at the cell centre. `width` / `height` are the
 * cell in container pixels; the art is fitted to the height.
 *
 * Imperative API (ref): spin(), stop(isManual), setLanding(symbolId, value),
 * show(symbolId, value), hide(), freeze(), getController().
 *
 * `onLanded(reelIndex, rowIndex, symbolId, value)` fires when a spin has
 * landed and the static layer shows the result — the board hands the cell to
 * the overlay spine from there.
 */
const HoldAndWinCell = forwardRef(
  ({ width, height, reelIndex, rowIndex, symbolId, spritesheet, onLanded }, ref) => {
    const staticLayerRef = useRef(null);
    const strip = useRef({ container: null, controller: null });
    const staticSprite = useRef(null);
    const valueText = useRef(null);
    const shown = useRef({ symbolId, value: null });
    const landing = useRef({ symbolId: null, value: null });
    const onLandedRef = useRef(onLanded);
    onLandedRef.current = onLanded;

    // The mask lives in state: the layer's `mask` prop reads it, and a ref
    // alone is still null on the first render.
    const [maskNode, setMaskNode] = useState(null);

    const drawMask = useCallback(
      (g) => {
        g.clear();
        g.beginFill(0x000000, 1);
        g.drawRect(-width / 2, -height / 2, width, height);
        g.endFill();
      },
      [width, height],
    );

    const artScale = useCallback(
      (id) => {
        const pay = symbolData[id]?.symbolPay || "";
        const category =
          Settings.SYMBOL_SCALE_OVERRIDES?.[pay] ??
          Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT ??
          1;
        return (height / Settings.SYMBOL_HEIGHT) * category;
      },
      [height],
    );

    // Paint the static layer: texture + (coins only) the value.
    const paintStatic = useCallback(
      (id, value) => {
        const layer = staticLayerRef.current;
        if (!layer) return;
        shown.current = { symbolId: id, value };

        const info = id != null ? symbolData[id] : null;
        const texture =
          info?.texture && spritesheet?.textures?.[`${info.texture}.png`];

        if (!staticSprite.current) {
          const sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
          sprite.anchor.set(0.5);
          sprite.roundPixels = true;
          layer.addChild(sprite);
          staticSprite.current = sprite;
        }
        staticSprite.current.texture = texture || PIXI.Texture.EMPTY;
        staticSprite.current.alpha = texture ? 1 : 0.001;
        staticSprite.current.scale.set(artScale(id));

        const text =
          info && isCoinSymbol(id) && value > 0 ? formatToCustomLocale(value) : "";
        if (!valueText.current && text) {
          // A Sprite from the label cache (coinLabel.js): crisp, never rounded.
          const label = createCoinLabel();
          label.zIndex = 1;
          layer.addChild(label);
          valueText.current = label;
        }
        setCoinLabel(
          valueText.current,
          text,
          height * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
          width * 0.9,
        );
      },
      [spritesheet, artScale, height, width],
    );

    const hideStatic = () => {
      if (staticSprite.current) staticSprite.current.alpha = 0.001;
      if (valueText.current) valueText.current.alpha = 0;
    };

    // ─── Build: static layer, then the spinning strip behind it ───
    useEffect(() => {
      const layer = staticLayerRef.current;
      if (!layer || !spritesheet?.textures) return undefined;
      paintStatic(symbolId, null);

      const parent = layer.parent;
      const container = new PIXI.Container();
      container.roundPixels = true;
      // The visible strip sprite (index 2) sits on the cell centre.
      container.y = -2 * height;
      parent.addChildAt(container, parent.getChildIndex(layer));

      const controller = new HoldAndWinCellController(
        container,
        symbolId,
        width,
        height,
        rowIndex,
        reelIndex,
        null,
        spritesheet,
      );
      controller.onStopCallback = () => {
        const { symbolId: id, value } = landing.current;
        paintStatic(id, value);
        onLandedRef.current?.(reelIndex, rowIndex, id, value);
      };
      strip.current = { container, controller };

      return () => {
        controller.cleanup();
        container.parent?.removeChild(container);
        container.destroy({ children: true });
        strip.current = { container: null, controller: null };
        staticSprite.current = null;
        valueText.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spritesheet]);

    // ─── Resize ───
    useEffect(() => {
      strip.current.controller?.resize(width, height);
      const { controller, container } = strip.current;
      if (container && !controller?._pendingPositionUpdate) {
        container.y = -2 * height;
      }
      // Repainting re-applies the label at the new size.
      paintStatic(shown.current.symbolId, shown.current.value);
    }, [width, height, paintStatic]);

    useImperativeHandle(
      ref,
      () => ({
        spin: () => {
          const controller = strip.current.controller;
          if (!controller) return;
          hideStatic();
          controller.startSpinNoWindUp();
        },
        stop: (isManual = false) => strip.current.controller?.stopSpin(isManual),
        setLanding: (id, value = null) => {
          landing.current = { symbolId: id, value };
          strip.current.controller?.setLandingValue(value);
          strip.current.controller?.setLandingSymbolId(id);
        },
        show: (id, value = null) => paintStatic(id, value),
        hide: () => hideStatic(),
        freeze: () => strip.current.controller?.freezeSpin(),
        getController: () => strip.current.controller,
      }),
      [paintStatic],
    );

    return (
      <Container name={`hnw_cell_${reelIndex}_${rowIndex}`}>
        <Graphics ref={setMaskNode} draw={drawMask} />
        <Container mask={maskNode}>
          <Container ref={staticLayerRef} sortableChildren />
        </Container>
      </Container>
    );
  },
);

HoldAndWinCell.displayName = "HoldAndWinCell";

export default memo(HoldAndWinCell);
